import { useEffect, useRef, useState } from 'react';
import { measureVoiceEnergy } from '../lib/voiceEnergyAnalysis';

export default function useMicLevel(isActive = true) {
  const [micLevel, setMicLevel] = useState(0);
  const [permissionState, setPermissionState] = useState('idle');

  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const rafRef = useRef(null);
  const dataArrayRef = useRef(null);

  useEffect(() => {
    if (!isActive) {
      setMicLevel(0);
      setPermissionState((prev) => (prev === 'denied' ? prev : 'idle'));
      return undefined;
    }

    let cancelled = false;

    async function start() {
      try {
        setPermissionState('requesting');

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioContext = new AudioCtx();
        audioContextRef.current = audioContext;

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.2;
        analyser.minDecibels = -100;
        analyser.maxDecibels = -8;
        analyserRef.current = analyser;

        const source = audioContext.createMediaStreamSource(stream);
        sourceRef.current = source;
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.fftSize);
        dataArrayRef.current = dataArray;

        setPermissionState('granted');

        let smoothed = 0;

        const updateLevel = () => {
          if (!analyserRef.current || !dataArrayRef.current || cancelled) {
            return;
          }

          analyserRef.current.getByteTimeDomainData(dataArrayRef.current);

          smoothed = measureVoiceEnergy(dataArrayRef.current, smoothed).level;
          setMicLevel(smoothed);

          rafRef.current = window.requestAnimationFrame(updateLevel);
        };

        updateLevel();
      } catch (error) {
        console.error('Microphone access failed:', error);
        setPermissionState('denied');
        setMicLevel(0);
      }
    }

    start();

    return () => {
      cancelled = true;

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }

      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {}
      }

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }

      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {}
      }

      streamRef.current = null;
      audioContextRef.current = null;
      analyserRef.current = null;
      sourceRef.current = null;
      dataArrayRef.current = null;
    };
  }, [isActive]);

  return { micLevel, permissionState };
}
